import { getAmapProxyBaseUrl } from "../services/amapProxyClient";
import { useEffect, useMemo, useState } from "react";
import type { GeoPoint } from "../config/amapChargingDemo";
import type { WaitingRouteBandId } from "../config/amapWaitingRouteDemo";
import {
  createFallbackTimedDetourPlan,
  planTimedDetourRoute,
  type TimedDetourPlan
} from "../services/timedDetourPlanner";

const PLAN_DEBOUNCE_MS = 220;

export function useTimedDetourRoute({
  bandId,
  enabled,
  origin,
  revision
}: {
  bandId: WaitingRouteBandId;
  enabled: boolean;
  origin: GeoPoint;
  revision: number;
}) {
  const fallbackPlan = useMemo(
    () => createFallbackTimedDetourPlan({ bandId, origin }),
    [bandId, origin]
  );
  const [plan, setPlan] = useState<TimedDetourPlan>(fallbackPlan);

  useEffect(() => {
    let cancelled = false;
    const webServiceKey = getAmapProxyBaseUrl();

    if (!enabled) {
      setPlan(fallbackPlan);
      return undefined;
    }

    if (!webServiceKey) {
      setPlan(fallbackPlan);
      return undefined;
    }

    setPlan(createFallbackTimedDetourPlan({
      bandId,
      origin,
      status: "loading"
    }));

    const planningTimer = window.setTimeout(() => {
      planTimedDetourRoute({
        bandId,
        origin,
        webServiceKey
      })
        .then((nextPlan) => {
          if (!cancelled) {
            setPlan(nextPlan);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setPlan({
              ...fallbackPlan,
              unavailableReason: "amap-route-unavailable"
            });
          }
        });
    }, PLAN_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(planningTimer);
    };
  }, [bandId, enabled, fallbackPlan, origin, revision]);

  return plan.bandId === bandId ? plan : fallbackPlan;
}
